import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import AvatarModal from './AvatarModal';
import EquipmentSelectModal from './EquipmentSelectModal';
import EquipmentHistoryModal from './EquipmentHistoryModal';
import SettingsModal from './SettingsModal';
import PresetModelDetailModal from './PresetModelDetailModal';
import LandingPage from './LandingPage';
import debugHelper from '../../admin/utils/debugHelper';
import avatarCache from '../../common/utils/AvatarCache';

// Phase 1 i18n — translator.trans() 返回 vnode array，flatten 为 string。
// WHY: see memory/flarum-1.8-pitfalls.md 坑 B
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}

// 有占位符的翻译（"已选 N 件"），保留 vnode array 让 mithril 当 children 渲染。
// WHY: t() flatten 会丢 vnode，导致 rich placeholder 失效。tRich() 保留 array 结构。
function tRich(key, params) {
  return app.translator.trans(key, params || {});
}

// 装备类型定义（label 改 function lazy 调用，避免 module top-level 时 translator 未就绪）
const EQUIPMENT_TYPES = [{
  key: 'clothing',
  label: () => t('ziven-dress-up.forum.common.equipment_clothing'),
  icon: 'fa-tshirt'
}, {
  key: 'pants',
  label: () => t('ziven-dress-up.forum.common.equipment_pants'),
  icon: 'fa-vest-patches'
}, {
  key: 'helmet',
  label: () => t('ziven-dress-up.forum.common.equipment_helmet'),
  icon: 'fa-hard-hat'
}, {
  key: 'boots',
  label: () => t('ziven-dress-up.forum.common.equipment_boots'),
  icon: 'fa-shoe-prints'
}, {
  key: 'gloves',
  label: () => t('ziven-dress-up.forum.common.equipment_gloves'),
  icon: 'fa-mitten'
}, {
  key: 'goggles',
  label: () => t('ziven-dress-up.forum.common.equipment_goggles'),
  icon: 'fa-glasses'
}, {
  key: 'snowboard',
  label: () => t('ziven-dress-up.forum.common.equipment_snowboard'),
  icon: 'fa-person-skiing'
}, {
  key: 'backpack',
  label: () => t('ziven-dress-up.forum.common.equipment_backpack'),
  icon: 'fa-bag-shopping'
}, {
  key: 'background',
  label: () => t('ziven-dress-up.forum.common.equipment_background'),
  icon: 'fa-image'
}];

// 装备类型映射表（用于快速查找）
const EQUIPMENT_TYPE_MAP = {};
EQUIPMENT_TYPES.forEach(item => {
  EQUIPMENT_TYPE_MAP[item.key] = item;
});

// 背景类型定义（用于提取背景图片）
const BACKGROUND_TYPES = ['background'];
export default class SeedreamPage extends Page {
  constructor() {
    super(...arguments);
    // v0.4.0 round 2 fix: Flarum 1.8 Page.bodyClass 写到 #app (不是 body),
    // 用 class selector 隔离 ziven-dress-up 路由范围 = 不污染 Flarum 全局
    // 覆盖 /dressUp + /dressUp/history 两个路由（项目内 ziven-calendar App--calendar pattern）
    _defineProperty(this, "bodyClass", 'App--dress-up');
  }
  oninit(vnode) {
    super.oninit(vnode);

    // #362 v0.4.7b 返工 — 前端权限/登录态守卫
    //   三态: 'ok' (主流程) / 'login_required' (未登录) / 'no_permission' (无 seedream.use)
    //   后端所有 controller 已 hasPermission('seedream.use') 强制检查
    //   （参考 SessionLoginController 等），但用户从浏览器直接访问 /dressUp
    //   仍会渲染空 shell + 触发 /api/ziven/seedream/limit/check。
    //   前端 gate 提前拦截，零 API 请求 + 友好提示 + 复用 Flarum 内置 LogInModal/SignUpModal。
    //
    //   #362 v0.4.7a 教训：不要用 app.session.user.can('seedream.use')！
    //   Flarum 1.8 JS User model 没有通用 .can() 方法（只有 canEdit/canDelete 等 specific 方法），
    //   can undefined 会让守卫误判所有登录用户为 no_permission。改用 extend.php L55-58
    //   ForumSerializer 暴露的 forum attribute 'seedreamAllowUse'（actor.hasPermission 结果）。
    //
    //   seedreamAllowUse 在匿名用户（app.session.user 不存在）时序列化器仍会调
    //   （actor 是 guest，hasPermission 返 false），所以 app.forum.attribute
    //   对未登录也安全 — 但我们还是先判 app.session.user 给未登录一个独立的
    //   "请登录" UI，比直接看到"无权访问"更友好。
    this.guardState = 'ok';
    if (!app.session.user) {
      this.guardState = 'login_required';
    } else {
      // 登录用户：从 forum attribute 读 hasPermission('seedream.use') 结果
      // attribute 未就绪（论坛属性 bootstrap 还没跑完）时，attr 会是 undefined，
      // 此时暂列 ok，等下次 oninit/redraw 再判（保守 fail-open，不至于误拦登录用户）
      const hasPermission = app.forum.attribute('seedreamAllowUse');
      if (hasPermission === false) {
        this.guardState = 'no_permission';
      }
    }

    // COS配置
    this.cosEnabled = app.forum.attribute('seedreamCosEnabled') === true;
    this.cosCdnDomain = app.forum.attribute('seedreamCosCdnDomain') || '';

    // 我的形象（从服务器获取的已选形象）
    this.selectedAvatar = null; // { id, avatar_base64, is_selected }

    // v0.3.0 预设模特 (PresetModel) - 跟"我的形象"互斥，融合时替代
    this.selectedPresetModel = null; // { id, name, image_url, gender, category }
    this.presetModels = []; // 当前页 list
    this.presetModelsPage = 1;
    this.presetModelsTotal = 0;
    this.presetModelsLoading = false;
    this.presetModelsLoaded = false; // v0.4.7: 首次加载完标记，刷新页面重置，避免重复请求
    this.presetModelsError = ''; // v0.4.8: 加载失败提示文案（空字符串 = 无错误，渲染错误态用）

    // 装备照片数组：每张照片包含文件、预览URL和对应的装备类型数组
    // 结构: [{ id: string, file: File, dataUrl: string, types: string[], cosUrl: string }]
    this.equipmentPhotos = [];

    // 状态
    this.isProcessing = false;
    this.resultImage = Stream('');
    this.errorMessage = Stream('');
    this.success = Stream(false);
    this.maxFileSize = app.forum.attribute('seedreamMaxFileSize') || 10;

    // 拖拽状态计数器（解决子元素触发dragleave的问题）
    this.dragCounter = {};

    // 每日使用限制
    this.dailyLimit = 0;
    this.todayUsage = 0;
    this.remaining = 0;
    this.canUse = true;

    // 进度
    this.progress = Stream(0);
    this.statusMessage = Stream(t('ziven-dress-up.forum.page.status_preparing'));
    this.taskId = Stream('');
    this.pollingInterval = null;

    // 检查可灵回调是否启用（回调启用时前端不显示轮询进度）
    this.klingCallbackEnabled = app.forum.attribute('seedreamKlingCallbackEnabled') === true;

    // #362 v0.4.7 [P1] — 守卫前置：未登录 / 无权限时不调任何 seedream API
    //   /api/ziven/seedream/avatars 和 /api/ziven/seedream/limit/check 都已被守卫拦掉
    //   （V 测 #1: 未登录访问 /dressUp 应零 API 请求；#2: 无权限同样零请求）
    if (this.guardState === 'ok') {
      // 加载已选形象
      this.loadSelectedAvatar();

      // 加载每日使用限制
      this.loadLimitInfo();
    }
    const _this = this;
    this.selectedAvatarTab = "myAvatar";
    this.selectedUploadTab = "singleImage";
    this.avatarTabList = [{
      name: t('ziven-dress-up.forum.page.tab_my_avatar'),
      key: "myAvatar",
      event: function (e, tabKey) {
        _this.selectedAvatarTab = tabKey;
      }
    }, {
      name: t('ziven-dress-up.forum.page.tab_preset'),
      key: "officialAvatar",
      event: function (e, tabKey) {
        _this.selectedAvatarTab = tabKey;
        // v0.4.7: 切到预设模特 tab 时自动加载（只一次，除非刷新）
        // fallback: 自动加载失败时 presetModelsLoaded 仍 false，用户可手动点"点击重试"
        if (_this.presetModels.length === 0 && !_this.presetModelsLoading && !_this.presetModelsLoaded) {
          _this.loadPresetModels();
        }
      }
    }];
  }
  async loadLimitInfo() {
    debugHelper.log('Loading limit info...');
    try {
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/limit/check'
      });
      if (response.success && response.data) {
        this.dailyLimit = response.data.daily_limit || 0;
        this.todayUsage = response.data.today_usage || 0;
        this.remaining = response.data.remaining || 0;
        this.canUse = response.data.can_use !== false;
        debugHelper.log('Limit info loaded', response.data);
        m.redraw();
      } else {
        debugHelper.warn('Failed to load limit info', response);
      }
    } catch (error) {
      debugHelper.error('Failed to load limit info', error);
    }
  }
  async loadSelectedAvatar() {
    debugHelper.log('Loading selected avatar...');

    // 首先尝试从缓存加载
    try {
      const cachedAvatar = await avatarCache.getSelectedAvatar();
      if (cachedAvatar && cachedAvatar.avatar_base64) {
        debugHelper.log('Loaded avatar from cache:', cachedAvatar.id);
        this.selectedAvatar = {
          id: cachedAvatar.id,
          avatar_url: cachedAvatar.avatar_url,
          avatar_base64: cachedAvatar.avatar_base64,
          is_selected: true
        };
        m.redraw();
      }
    } catch (error) {
      debugHelper.error('Failed to load from cache:', error);
    }

    // 然后从服务器获取最新数据
    try {
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/avatars'
      });
      if (response.success && response.data) {
        // 找到选中的形象
        const selected = response.data.find(a => a.is_selected);
        if (selected) {
          var _this$selectedAvatar;
          this.selectedAvatar = {
            id: selected.id,
            avatar_url: selected.avatar_url,
            avatar_base64: ((_this$selectedAvatar = this.selectedAvatar) == null ? void 0 : _this$selectedAvatar.avatar_base64) || null,
            is_selected: true
          };

          // 如果缓存中没有 base64，异步下载并缓存
          if (!this.selectedAvatar.avatar_base64 && selected.avatar_url) {
            this.downloadAndCacheAvatar(selected);
          }
          debugHelper.log('Avatar loaded from server:', selected.id);
        } else if (!this.selectedAvatar) {
          debugHelper.log('No avatar selected');
        }
        m.redraw();
      } else {
        debugHelper.warn('Failed to load avatars', response);
      }
    } catch (error) {
      debugHelper.error('Failed to load selected avatar from server:', error);
    }
  }

  /**
   * v0.3.0: 加载预设模特列表 (lazy load + 不存 client storage)
   * 点 tab 或点 "加载更多" 时调, 缓存到 instance 不入 IndexedDB
   */
  async loadPresetModels() {
    // v0.4.8: 函数开头清掉旧错误（重试时 loading 态能盖过错误态；成功后也保持无错误）
    this.presetModelsError = '';
    this.presetModelsLoading = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/preset-models/list?page=' + this.presetModelsPage + '&per_page=20'
      });
      if (response.success && response.data) {
        var _response$data$pagina;
        const items = response.data.items || [];
        if (this.presetModelsPage === 1) {
          this.presetModels = items;
        } else {
          this.presetModels = this.presetModels.concat(items);
        }
        this.presetModelsTotal = ((_response$data$pagina = response.data.pagination) == null ? void 0 : _response$data$pagina.total) || 0;
        this.presetModelsPage++;
        // v0.4.7: 首次加载成功标记（presetModelsLoaded 之前为 false 才是首次）
        // catch 分支不置 true，保留重试入口
        if (!this.presetModelsLoaded) {
          this.presetModelsLoaded = true;
        }
        debugHelper.log('Preset models loaded:', this.presetModels.length, '/', this.presetModelsTotal);
      }
    } catch (error) {
      // v0.4.8: catch 分支加 user-facing 错误提示（修复 #319 静默失败）
      // 之前：只 console.error + loading=false，UI 仍显示空态（误导用户）
      // 现在：设 presetModelsError 字符串，UI 渲染红色错误态 + 重试按钮
      debugHelper.error('Failed to load preset models:', error);
      this.presetModelsError = t('ziven-dress-up.forum.page.preset_load_error');
    }
    this.presetModelsLoading = false;
    m.redraw();
  }

  /**
   * 异步下载头像并缓存（只缓存当前选中的）
   */
  async downloadAndCacheAvatar(avatar) {
    if (!avatar.avatar_url) return;
    try {
      const response = await fetch(avatar.avatar_url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        await avatarCache.saveSelectedAvatar({
          id: avatar.id,
          avatar_url: avatar.avatar_url,
          avatar_base64: base64
        });
        debugHelper.log('Avatar cached:', avatar.id);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      debugHelper.error('Failed to download and cache avatar:', avatar.id, error);
    }
  }

  /**
   * #366 v0.4.8 — 守卫界面渲染
   *   - login_required → 渲染完整 LandingPage（产品官网风格 8 模块 + 弹 LogInModal）
   *   - no_permission   → 玻璃卡（保留 #362 行为，不弹落地页以免误导）
   */
  renderGuardView() {
    // #366 未登录 → 渲染 LandingPage，CTA 弹 Flarum LogInModal（不开新页）
    if (this.guardState === 'login_required') {
      return m(LandingPage, null);
    }

    // no_permission 仍用 #362 的玻璃卡（用户已登录但缺权限，不应看到营销页）
    return m("div", {
      class: "SeedreamPage SeedreamPage--guarded",
      style: {
        overflow: 'visible',
        minHeight: '100vh'
      }
    }, m("div", {
      class: "seedream-container",
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 80px)'
      }
    }, m("div", {
      class: "glass-card seedream-guard-card",
      style: {
        maxWidth: '460px',
        width: '100%',
        textAlign: 'center',
        padding: '48px 36px',
        // 紫底 + amber 描边（继承 :root --seedream-grad-purple 视觉语言）
        borderColor: 'var(--seedream-accent)',
        boxShadow: '0 0 32px rgba(251, 191, 36, 0.45), 0 8px 32px rgba(0, 0, 0, 0.35)'
      }
    }, m("div", {
      style: {
        fontSize: '48px',
        marginBottom: '16px',
        color: 'var(--seedream-accent)'
      }
    }, m("i", {
      class: "fas fa-shield-halved"
    })), m("h3", {
      style: {
        margin: '0 0 12px 0',
        fontSize: '20px',
        color: 'var(--seedream-text-primary)',
        fontWeight: '700'
      }
    }, app.translator.trans('ziven-dress-up.forum.guard.no_permission_title')), m("p", {
      style: {
        margin: '0 0 28px 0',
        fontSize: '14px',
        lineHeight: '1.6',
        color: 'var(--seedream-text-secondary)'
      }
    }, app.translator.trans('ziven-dress-up.forum.guard.no_permission_body')), m("div", {
      style: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }
    }, m(Button, {
      class: "btn btn-amber",
      onclick: () => m.route.set(app.route('index'))
    }, m("i", {
      class: "fas fa-house"
    }), ' ' + app.translator.trans('ziven-dress-up.forum.guard.back_button'))))));
  }
  view() {
    // #362 v0.4.7 [P1] + #366 v0.4.8 — 守卫 UI 前置渲染
    //   情况 1（未登录）：#366 升级为 LandingPage（产品官网风格 + 弹 LogInModal）
    //   情况 2（无权限）：#362 玻璃风卡片（紫底 + amber 描边）+ amber "返回首页" 按钮
    //   用 m.route.set('/') 跳首页，与 ziven-calendar 等项目内的 home 行为一致
    if (this.guardState !== 'ok') {
      return this.renderGuardView();
    }

    // 检查是否有至少一张装备图片
    const hasEquipment = this.equipmentPhotos.length > 0;
    const canSubmit = this.selectedAvatar && hasEquipment && !this.isProcessing && this.canUse;
    const SlideTab = window.zivenCore.SlideTab;

    // v0.4.0 Phase 2 批 2A: 重写 markup 为 mockup Page1 视觉规范
    // 保留 100% state / 方法 / modal 引用 / API 调用, 仅改渲染结构
    // 2 sections: 模特选择 + 装备照片 + 全宽琥珀金 Start Fusion 按钮
    return m("div", {
      class: "SeedreamPage",
      style: {
        overflow: 'visible',
        minHeight: '100vh'
      }
    }, m("div", {
      class: "seedream-container"
    }, m("h2", {
      class: "seedream-title"
    }, m("i", {
      class: "fas fa-magic"
    }), t('ziven-dress-up.forum.page.title')), m("div", {
      class: "seedream-header-actions"
    }, m("span", {
      class: "limit-badge"
    }, tRich('ziven-dress-up.forum.page.remaining_label', {
      used: this.remaining,
      total: this.dailyLimit
    })), m(Button, {
      class: "btn-history-link",
      onclick: () => m.route.set('/dressUp/history')
    }, m("i", {
      class: "fas fa-history"
    }), t('ziven-dress-up.forum.page.history_button')), m(Button, {
      class: "btn-settings-link",
      onclick: () => this.openSettingsModal()
    }, m("i", {
      class: "fas fa-cog"
    }), t('ziven-dress-up.forum.page.settings_button'))), m("div", {
      class: "seedream-section seedream-models-section",
      style: {
        opacity: this.isProcessing ? 0.5 : 1,
        pointerEvents: this.isProcessing ? 'none' : 'auto'
      }
    }, m("div", {
      class: "seedream-tab-switch"
    }, m(SlideTab, {
      tabList: this.avatarTabList,
      showTabLine: false
    })), this.selectedAvatarTab === "myAvatar" && m("div", {
      class: "seedream-my-avatar"
    }, this.selectedAvatar ? m("div", {
      class: "avatar-preview",
      onclick: () => !this.isProcessing && this.openAvatarModal()
    }, m("img", {
      src: this.selectedAvatar.avatar_base64 || this.selectedAvatar.avatar_url,
      alt: "Selected avatar",
      onclick: e => {
        e.stopPropagation();
        if (!this.isProcessing) app.fancyboxOpen(this.selectedAvatar.avatar_base64 || this.selectedAvatar.avatar_url);
      },
      style: {
        cursor: this.isProcessing ? 'default' : 'pointer'
      }
    }), !this.isProcessing && m("div", {
      class: "change-hint"
    }, t('ziven-dress-up.forum.page.click_change'))) : m("div", {
      class: "seedream-avatar-placeholder",
      onclick: () => !this.isProcessing && this.openAvatarModal()
    }, m("i", {
      class: "fas fa-user-circle"
    }), m("span", null, " ", t('ziven-dress-up.forum.page.select_my_avatar')), m("p", null, " ", t('ziven-dress-up.forum.page.supported_formats')))), this.selectedAvatarTab === "officialAvatar" && m("div", {
      class: "seedream-model-grid"
    }, this.presetModelsLoading && this.presetModels.length === 0 && m("div", {
      class: "seedream-models-loading"
    }, m(LoadingIndicator, {
      size: "medium"
    }), m("span", null, " ", t('ziven-dress-up.forum.common.loading'))), !this.presetModelsLoading && this.presetModels.length === 0 && this.presetModelsLoaded && m("div", {
      class: "seedream-models-empty"
    }, m("i", {
      class: "fas fa-images"
    }), m("span", null, " ", t('ziven-dress-up.forum.page.preset_empty'))), !this.presetModelsLoading && this.presetModels.length === 0 && !this.presetModelsLoaded && !this.presetModelsError && m("div", {
      class: "seedream-models-empty",
      onclick: () => this.loadPresetModels()
    }, m("i", {
      class: "fas fa-images"
    }), m("span", null, " ", t('ziven-dress-up.forum.page.preset_load_click'))), !this.presetModelsLoading && this.presetModels.length === 0 && this.presetModelsError && m("div", {
      class: "seedream-models-error",
      role: "alert",
      onclick: () => this.loadPresetModels()
    }, m("i", {
      class: "fas fa-exclamation-circle seedream-models-error-icon"
    }), m("div", {
      class: "seedream-models-error-body"
    }, m("strong", null, " ", t('ziven-dress-up.forum.page.preset_load_failed')), m("span", null, ' ' + this.presetModelsError)), m("div", {
      class: "seedream-models-error-retry"
    }, m("i", {
      class: "fas fa-redo"
    }), t('ziven-dress-up.forum.page.click_retry'))), this.presetModels.length > 0 && m('[', null, this.presetModels.slice(0, 6).map(pm => m("div", {
      key: pm.id,
      class: 'model-card glass-card' + (this.selectedPresetModel && this.selectedPresetModel.id === pm.id ? ' is-selected' : '')
      // v0.4.0 Phase 2 批 2C-1: model-card click 弹 PresetModelDetailModal
      // (替换旧直选逻辑 → 弹详情 modal 确认后才设 state)
      ,
      onclick: () => !this.isProcessing && this.openPresetModelDetail(pm)
    }, m("img", {
      class: "model-card-image",
      src: pm.image_url,
      alt: pm.name,
      loading: "lazy"
    }), m("div", {
      class: "model-card-name"
    }, pm.name), this.selectedPresetModel && this.selectedPresetModel.id === pm.id && m("div", {
      class: "model-card-check"
    }, m("i", {
      class: "fas fa-check-circle"
    })))), this.presetModelsLoading && this.presetModels.length > 0 && m(LoadingIndicator, {
      size: "small"
    }), this.presetModels.length < this.presetModelsTotal && !this.presetModelsLoading && m("div", {
      class: "seedream-models-loadmore",
      onclick: () => this.loadPresetModels()
    }, m(Button, {
      class: "Button Button--small"
    }, tRich('ziven-dress-up.forum.page.load_more_with_count', {
      loaded: this.presetModels.length,
      total: this.presetModelsTotal
    }), "}"))))), m("div", {
      class: "seedream-section seedream-equipment-section",
      style: {
        opacity: this.isProcessing ? 0.5 : 1,
        pointerEvents: this.isProcessing ? 'none' : 'auto'
      }
    }, m("div", {
      class: "seedream-equipment-strip"
    }, m("h3", null, m("i", {
      class: "fas fa-tshirt"
    }), t('ziven-dress-up.forum.page.equipment_photos_title')), m("p", null, " ", t('ziven-dress-up.forum.page.equipment_photos_note'))), !this.isProcessing && m("div", {
      class: "seedream-history-link-row"
    }, m(Button, {
      class: "seedream-history-link",
      onclick: () => this.openEquipmentHistoryModal(),
      title: t('ziven-dress-up.forum.page.history_equipment_title')
    }, m("i", {
      class: "fas fa-clock-rotate-left"
    }), t('ziven-dress-up.forum.page.history_equipment_button'))), m("div", {
      class: "selected-equipment-list"
    }, this.equipmentPhotos.map((photo, index) => {
      const primaryType = EQUIPMENT_TYPE_MAP[photo.types[0]];
      if (!primaryType) return null;
      return m("div", {
        class: "selected-equipment-item",
        key: photo.id
      }, m("div", {
        class: "equipment-thumbnail"
      }, m("img", {
        src: photo.dataUrl,
        alt: "Equipment",
        onclick: () => app.fancyboxOpen(photo.dataUrl),
        style: {
          cursor: 'pointer'
        }
      })), m("div", {
        class: "equipment-info"
      }, m("div", {
        class: "equipment-type-tags"
      }, photo.types.map(typeKey => {
        const typeInfo = EQUIPMENT_TYPE_MAP[typeKey];
        if (!typeInfo) return null;
        return m("span", {
          class: "type-tag",
          key: typeKey
        }, m("i", {
          class: "fas ".concat(typeInfo.icon)
        }), typeof typeInfo.label === 'function' ? typeInfo.label() : typeInfo.label);
      }))), !this.isProcessing && m("div", {
        class: "equipment-actions"
      }, m("button", {
        class: "btn-reselect",
        onclick: () => this.reselectEquipment(index),
        title: t('ziven-dress-up.forum.page.reselect')
      }, m("i", {
        class: "fas fa-sync-alt"
      })), m("button", {
        class: "btn-delete",
        onclick: () => this.removeImage(index),
        title: t('ziven-dress-up.forum.page.delete')
      }, m("i", {
        class: "fas fa-trash-alt"
      }))));
    }), !this.isProcessing && m("div", {
      class: "add-equipment-btn",
      onclick: () => this.openEquipmentModal()
    }, m("i", {
      class: "fas fa-plus-circle"
    }), m("span", null, t('ziven-dress-up.forum.page.add_equipment')))), m("input", {
      ref: el => this.reselectFileInput = el,
      type: "file",
      accept: "image/jpeg,image/png",
      style: {
        display: 'none'
      },
      onchange: e => this.handleReselectFile(e)
    })), m("div", {
      class: "seedream-actions"
    }, m(Button, {
      class: "btn-amber btn-start-fusion",
      disabled: !canSubmit,
      onclick: () => this.startFusion()
    }, this.isProcessing ? m("span", null, m(LoadingIndicator, null), m("span", {
      class: "btn-text"
    }, tRich('ziven-dress-up.forum.page.generating_with_progress', {
      progress: this.progress()
    }))) : m("span", null, m("i", {
      class: "fas fa-magic"
    }), t('ziven-dress-up.forum.page.start_fusion')))), this.isProcessing && !this.klingCallbackEnabled && m("div", {
      class: "progress-area"
    }, m("div", {
      class: "progress-bar"
    }, m("div", {
      class: "progress-fill",
      style: {
        width: this.progress() + '%'
      }
    })), m("div", {
      class: "progress-text"
    }, m("span", {
      class: "status-message"
    }, this.statusMessage()), m("span", {
      class: "progress-percent"
    }, this.progress(), "%"))), this.isProcessing && this.klingCallbackEnabled && m("div", {
      class: "callback-hint-area"
    }, m("div", {
      class: "callback-hint"
    }, m("i", {
      class: "fas fa-clock"
    }), m("span", null, t('ziven-dress-up.forum.page.task_callback_hint')))), this.errorMessage() && m("div", {
      class: "error-message"
    }, this.errorMessage()), this.success() && this.resultImage() && m("div", {
      class: "result-area"
    }, m("h3", null, t('ziven-dress-up.forum.page.result_title')), m("img", {
      src: this.resultImage(),
      alt: "Fusion result",
      onclick: () => app.fancyboxOpen(this.resultImage()),
      style: {
        cursor: 'pointer'
      }
    }), m("div", {
      class: "result-actions"
    }, m(Button, {
      class: "btn-download",
      onclick: () => this.downloadResult()
    }, m("i", {
      class: "fas fa-download"
    }), t('ziven-dress-up.forum.common.download')), m(Button, {
      class: "btn-reset",
      onclick: () => this.reset()
    }, m("i", {
      class: "fas fa-redo"
    }), t('ziven-dress-up.forum.common.reset'))))));
  }

  /**
   * 验证图片文件的 magic bytes (文件头)
   * 防止伪装成图片的恶意文件攻击
   */
  validateImageMagicBytes(uint8Array, mimeType) {
    if (uint8Array.length < 4) {
      return false;
    }

    // JPEG magic bytes: FF D8 FF
    const JPEG_MAGIC = [0xFF, 0xD8, 0xFF];

    // PNG magic bytes: 89 50 4E 47 0D 0A 1A 0A
    const PNG_MAGIC = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];

    // 检查 JPEG
    if (mimeType === 'image/jpeg') {
      let isJpeg = true;
      for (let i = 0; i < JPEG_MAGIC.length; i++) {
        if (uint8Array[i] !== JPEG_MAGIC[i]) {
          isJpeg = false;
          break;
        }
      }
      if (isJpeg) return true;
    }

    // 检查 PNG
    if (mimeType === 'image/png') {
      let isPng = true;
      for (let i = 0; i < PNG_MAGIC.length; i++) {
        if (uint8Array[i] !== PNG_MAGIC[i]) {
          isPng = false;
          break;
        }
      }
      if (isPng) return true;
    }
    return false;
  }

  // ========== 拖拽上传支持 ==========
  handleDragEnter(e, type) {
    // 只在桌面端启用（检测是否为触控设备）
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 初始化计数器
    if (!this.dragCounter[type]) {
      this.dragCounter[type] = 0;
    }
    this.dragCounter[type]++;

    // 添加拖拽样式
    const target = e.currentTarget;
    if (target && this.dragCounter[type] === 1) {
      target.classList.add('drag-over');
    }
  }
  handleDragOver(e) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();
  }
  handleDragLeave(e, type) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 减少计数器
    if (this.dragCounter[type]) {
      this.dragCounter[type]--;
    }

    // 只有当计数器为0时才移除样式
    const target = e.currentTarget;
    if (target && (!this.dragCounter[type] || this.dragCounter[type] <= 0)) {
      target.classList.remove('drag-over');
      this.dragCounter[type] = 0;
    }
  }
  handleDrop(e, type) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 重置计数器
    this.dragCounter[type] = 0;
    const target = e.currentTarget;
    if (target) {
      target.classList.remove('drag-over');
    }
    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    // 创建一个模拟的 input change 事件
    const file = files[0];
    const mockEvent = {
      target: {
        files: [file],
        value: file.name
      }
    };

    // 调用原有的文件处理方法
    this.handleFileSelect(mockEvent, type);
  }

  // 背景图片拖拽
  handleBgDragEnter(e) {
    this.handleDragEnter(e, 'background');
  }
  handleBgDragLeave(e) {
    this.handleDragLeave(e, 'background');
  }
  handleBgDrop(e) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 重置计数器
    this.dragCounter['background'] = 0;
    const target = e.currentTarget;
    if (target) {
      target.classList.remove('drag-over');
    }
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    const mockEvent = {
      target: {
        files: [files[0]],
        value: files[0].name
      }
    };
    this.handleBackgroundFileSelect(mockEvent);
  }

  /**
   * 删除装备照片
   * @param {number} index - 装备照片数组中的索引
   */
  removeImage(index) {
    if (index >= 0 && index < this.equipmentPhotos.length) {
      this.equipmentPhotos.splice(index, 1);
      m.redraw();
    }
  }

  /**
   * 处理背景图片选择
   */
  handleBackgroundFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型 (只支持 jpg 和 png)
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.errorMessage(t('ziven-dress-up.forum.common.invalid_type'));
      e.target.value = '';
      return;
    }

    // 验证文件大小
    if (file.size > this.maxFileSize * 1024 * 1024) {
      this.errorMessage(tRich('ziven-dress-up.forum.common.file_too_large', {
        size: this.maxFileSize
      }));
      e.target.value = '';
      return;
    }
    const self = this;
    const reader = new FileReader();
    reader.onload = function (event) {
      const dataUrl = event.target.result;

      // 验证 data URL
      if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
        self.errorMessage(t('ziven-dress-up.forum.common.read_error'));
        e.target.value = '';
        return;
      }

      // 从 data URL 中提取并验证 magic bytes
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data) {
        self.errorMessage(t('ziven-dress-up.forum.common.invalid_format'));
        e.target.value = '';
        return;
      }

      // 解码前几个字节进行 magic bytes 验证
      try {
        const binaryString = atob(base64Data.substring(0, 24));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const isValidImage = self.validateImageMagicBytes(bytes, file.type);
        if (!isValidImage) {
          self.errorMessage(t('ziven-dress-up.forum.common.invalid_content'));
          e.target.value = '';
          return;
        }
      } catch (err) {
        self.errorMessage(t('ziven-dress-up.forum.common.invalid_content_short'));
        e.target.value = '';
        return;
      }

      // 存储背景图片
      self.backgroundImage = file;
      self.backgroundPreview(dataUrl);

      // 清除input值，允许选择相同文件
      e.target.value = '';
      self.errorMessage('');
      self.success(false);
      m.redraw();
    };
    reader.onerror = function () {
      self.errorMessage(t('ziven-dress-up.forum.common.read_error'));
      e.target.value = '';
      m.redraw();
    };
    reader.readAsDataURL(file);
  }

  /**
   * 移除背景图片
   */
  removeBackgroundImage() {
    this.backgroundImage = null;
    this.backgroundPreview('');
    m.redraw();
  }
  async startFusion() {
    // v0.3.0: 形象必填 (我的形象 OR 预设模特 二选一)
    if (!this.selectedAvatar && !this.selectedPresetModel) {
      this.errorMessage(t('ziven-dress-up.forum.page.select_avatar_first'));
      return;
    }

    // 检查是否有至少一张装备图片
    if (this.equipmentPhotos.length === 0) {
      this.errorMessage(t('ziven-dress-up.forum.page.upload_equipment_first'));
      return;
    }

    // 检查剩余次数
    if (!this.canUse || this.remaining <= 0) {
      this.errorMessage(tRich('ziven-dress-up.forum.page.daily_quota_exceeded', {
        used: this.todayUsage,
        total: this.dailyLimit
      }));
      return;
    }

    // 使用次数已在后端预先记录，前端只需要更新显示
    debugHelper.log('Starting fusion, usage will be recorded by backend');
    this.isProcessing = true;
    this.progress(0);
    this.statusMessage(t('ziven-dress-up.forum.page.status_preparing_upload'));
    this.errorMessage('');
    m.redraw();
    try {
      const formData = new FormData();

      // 必须使用COS模式：先上传图片到COS，然后提交URL
      debugHelper.log('COS mode: uploading images to COS first');

      // 1. 首先上传人物图片到COS
      this.progress(5);
      this.statusMessage(t('ziven-dress-up.forum.page.status_upload_person'));
      m.redraw();
      let personImageUrl = this.selectedAvatar ? this.selectedAvatar.avatar_url : this.selectedPresetModel.image_url;
      if (!personImageUrl) {
        throw new Error(t('ziven-dress-up.forum.page.error_person_url_missing'));
      }

      // 如果人物图片URL不是COS地址，需要上传
      // 这里假设人物图片已经在COS上了（因为avatar_url是从服务端获取的）
      debugHelper.log('Person image URL:', personImageUrl);

      // 2. 上传装备图片到COS
      this.progress(10);
      this.statusMessage(t('ziven-dress-up.forum.page.status_upload_equipment'));
      m.redraw();
      const uploadedUrls = [];
      const totalPhotos = this.equipmentPhotos.length;
      for (let i = 0; i < totalPhotos; i++) {
        const photo = this.equipmentPhotos[i];
        const progress = 10 + Math.round(i / totalPhotos * 30);

        // v0.4.7 #364 — 历史来源复用 COS URL, 不重传
        if (photo.source === 'history' && photo.cosUrl) {
          this.progress(progress);
          this.statusMessage(tRich('ziven-dress-up.forum.page.status_reuse_equipment', {
            current: i + 1,
            total: totalPhotos
          }));
          m.redraw();
          uploadedUrls.push(photo.cosUrl);
          debugHelper.log('[Seedream] Reusing history equipment:', photo.cosUrl);
          continue;
        }
        this.progress(progress);
        this.statusMessage(tRich('ziven-dress-up.forum.page.status_upload_equipment_progress', {
          current: i + 1,
          total: totalPhotos
        }));
        m.redraw();
        try {
          const url = await this.uploadToCOS(photo.file);
          if (url) {
            uploadedUrls.push(url);
            // v0.4.7 #364 — 上传成功后回填 cosUrl, 让 photo 保持一致
            photo.cosUrl = url;
          } else {
            throw new Error(t('ziven-dress-up.forum.page.error_equipment_upload_failed'));
          }
        } catch (uploadError) {
          debugHelper.error('Equipment upload error:', uploadError);
          throw new Error(tRich('ziven-dress-up.forum.page.error_equipment_upload_failed_with_reason', {
            reason: uploadError.message
          }));
        }
      }
      if (uploadedUrls.length !== totalPhotos) {
        throw new Error(t('ziven-dress-up.forum.page.error_partial_upload_failed'));
      }

      // 3. 提交融合请求（使用COS URL）
      this.progress(40);
      this.statusMessage(t('ziven-dress-up.forum.page.status_submit_fusion'));
      m.redraw();

      // 人物图片URL
      formData.append('person_image_url', personImageUrl);
      // v0.3.0: 标记为预设模特 (FusionController 据此跳过 person_avatar_id 路径)
      if (this.selectedPresetModel) {
        formData.append('preset_model_id', String(this.selectedPresetModel.id));
        formData.append('person_image_source', 'preset_model');
      } else {
        formData.append('person_image_source', 'user_avatar');
      }

      // 装备图片URL数组
      formData.append('equipment_urls', JSON.stringify(uploadedUrls));

      // 添加装备类型映射（JSON格式）
      const equipmentTypes = this.equipmentPhotos.map(p => p.types);
      formData.append('equipment_types', JSON.stringify(equipmentTypes));
      debugHelper.log('Sending fusion request...');
      const response = await app.request({
        method: 'POST',
        url: '/api/ziven/seedream/fusion',
        body: formData
      });
      debugHelper.log('Fusion response:', response);
      if (response.success) {
        debugHelper.log('Response data:', response.data);

        // 更新剩余次数（后端返回的）
        if (response.data.remaining !== undefined) {
          this.remaining = response.data.remaining;
        }

        // v0.4.7 #364 — 记录装备使用到历史 (新上传的装备, 历史来源跳过)
        // fire-and-forget, 不阻塞主流程
        this.recordEquipmentHistory(uploadedUrls);
        if (response.data.task_id) {
          debugHelper.log('Got task_id:', response.data.task_id);
          this.taskId(response.data.task_id);

          // 重新检查回调开关状态（确保使用最新值）
          this.klingCallbackEnabled = app.forum.attribute('seedreamKlingCallbackEnabled') === true;

          // 根据回调模式决定显示内容
          if (this.klingCallbackEnabled) {
            // 回调模式：只显示简短提示，不显示进度条
            this.progress(30);
            this.statusMessage(t('ziven-dress-up.forum.page.status_task_submitted_wait'));
            // 回调模式下，resultImage 应该由回调触发后刷新页面获取
            // 暂时显示一个占位状态
          } else {
            // 轮询模式：显示详细进度
            this.progress(30);
            this.statusMessage(t('ziven-dress-up.forum.page.status_task_submitted_start'));
            m.redraw();
            this.startPolling();
          }
          m.redraw();
        } else if (response.data.image_url || response.data.image_base64) {
          debugHelper.log('Got direct image result');

          // 优先使用 API 返回的 image_url
          let imageUrl = response.data.image_base64 || response.data.image_url;
          // 如果有本地保存的路径，也可以使用（当 API URL 过期时）
          if (response.data.result_image_path) {
            debugHelper.log('Local path available:', response.data.result_image_path);
          }
          this.progress(100);
          this.statusMessage(t('ziven-dress-up.forum.page.status_generation_complete'));
          this.resultImage(imageUrl);
          this.success(true);
          this.isProcessing = false;
        } else {
          debugHelper.warn('No task_id and no image in response');
          this.errorMessage(t('ziven-dress-up.forum.page.error_api_no_image_or_task_id'));
          this.isProcessing = false;
        }
      } else {
        debugHelper.warn('Response not success:', response.error);
        this.errorMessage(response.error || t('ziven-dress-up.forum.common.fusion_failed'));
        this.isProcessing = false;
        // v0.0.7: 后端已 refund，统一从服务端拉全字段
        // (之前只更新 remaining，todayUsage/canUse 没改 → UI 字段不一致)
        await this.loadLimitInfo();
      }
    } catch (error) {
      debugHelper.error('Fusion error:', error);
      this.errorMessage(error.message || t('ziven-dress-up.forum.common.network_error'));
      this.isProcessing = false;
      // v0.0.7: 网络错误也统一从服务端拉（catch 分支没有 error.response.data 的话之前完全没更新）
      await this.loadLimitInfo();
    }
    m.redraw();
  }
  startPolling() {
    this.pollingInterval = setInterval(() => this.checkStatus(), 2000);
  }
  async checkStatus() {
    if (!this.taskId()) return;
    try {
      debugHelper.log('Checking status for task:', this.taskId());
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/status',
        params: {
          task_id: this.taskId()
        }
      });
      debugHelper.log('Status response:', response);
      if (response.success && response.data) {
        debugHelper.log('Status data:', response.data);
        this.progress(response.data.progress || 0);
        this.statusMessage(response.data.message || t('ziven-dress-up.forum.page.status_processing'));

        // 更新剩余次数（后端返回的）
        if (response.data.remaining !== undefined) {
          this.remaining = response.data.remaining;
        }
        if (response.data.status === 'completed') {
          debugHelper.log('Task completed!');
          this.progress(100);
          this.statusMessage(t('ziven-dress-up.forum.page.status_generation_complete'));
          this.stopPolling();
          if (response.data.image_url) {
            this.resultImage(response.data.image_url);
            this.success(true);
          }
          this.isProcessing = false;
        } else if (response.data.status === 'failed') {
          debugHelper.warn('Task failed');
          this.stopPolling();
          this.errorMessage(response.error || t('ziven-dress-up.forum.page.status_generation_failed'));
          this.isProcessing = false;
          // 更新剩余次数（后端已自动返还）
          if (response.data.remaining !== undefined) {
            this.remaining = response.data.remaining;
          }
        }
        m.redraw();
      } else {
        debugHelper.warn('Status check failed:', response);
      }
    } catch (error) {
      debugHelper.error('Status check error:', error);
    }
  }
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
  downloadResult() {
    if (!this.resultImage()) return;
    const link = document.createElement('a');
    link.href = this.resultImage();
    link.download = 'seedream-fusion-' + Date.now() + '.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  reset() {
    this.stopPolling();
    // 保留 selectedAvatar，不重置形象选择

    // 清空装备照片数组
    this.equipmentPhotos = [];
    this.resultImage('');
    this.errorMessage('');
    this.success(false);
    this.progress(0);
    this.statusMessage(t('ziven-dress-up.forum.page.status_preparing'));
    this.taskId('');
    m.redraw();
  }

  // v0.4.0 Phase 2 批 2C-1: 打开预设模特详情模态 (替换旧直选逻辑)
  openPresetModelDetail(pm) {
    // 如果已经选中同一个 model = 取消选中 (跟旧行为一致, 允许快速切换)
    if (this.selectedPresetModel && this.selectedPresetModel.id === pm.id) {
      this.selectedPresetModel = null;
      m.redraw();
      return;
    }
    app.modal.show(PresetModelDetailModal, {
      modelId: pm.id,
      onSelect: model => {
        this.selectedPresetModel = model;
        m.redraw();
      }
    });
  }
  openAvatarModal() {
    app.modal.show(AvatarModal, {
      selectedAvatar: this.selectedAvatar,
      onSelect: async avatar => {
        this.selectedAvatar = avatar;

        // 同步更新缓存（只保存当前选中的）
        if (avatar && avatar.avatar_url) {
          try {
            // 检查缓存中是否有 base64
            const cached = await avatarCache.getSelectedAvatar();
            if (cached && cached.avatar_base64) {
              this.selectedAvatar.avatar_base64 = cached.avatar_base64;
            } else {
              // 缓存中没有，立即下载并缓存
              await avatarCache.downloadAndCacheImage(avatar.avatar_url, avatar.id);
              const updated = await avatarCache.getSelectedAvatar();
              if (updated && updated.avatar_base64) {
                this.selectedAvatar.avatar_base64 = updated.avatar_base64;
              }
            }
          } catch (error) {
            debugHelper.error('Failed to sync avatar cache:', error);
          }
        }
        m.redraw();
      }
    });
  }

  /**
   * 打开装备选择Modal
   */
  openEquipmentModal() {
    // 获取已选择的类型（用于排除）
    const selectedTypes = this.equipmentPhotos.flatMap(p => p.types);
    app.modal.show(EquipmentSelectModal, {
      selectedEquipments: selectedTypes,
      onSubmit: (file, dataUrl, types) => {
        this.handleEquipmentSubmit(file, dataUrl, types);
      }
    });
  }

  /**
   * v0.4.7 #365 — 打开设置 Modal
   * 开关状态由 SettingsModal 内部 localStorage 持久化
   */
  openSettingsModal() {
    app.modal.show(SettingsModal);
  }

  /**
   * 处理装备提交（来自Modal）
   * @param {File} file - 上传的文件
   * @param {string} dataUrl - 图片预览URL
   * @param {Array} types - 选中的装备类型数组
   */
  handleEquipmentSubmit(file, dataUrl, types) {
    if (!types || types.length === 0) return;

    // 生成唯一ID（基于时间戳和随机数）
    const photoId = 'photo_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    // 添加到装备照片数组
    this.equipmentPhotos.push({
      id: photoId,
      file: file,
      dataUrl: dataUrl,
      types: [...types],
      cosUrl: null,
      // 上传来源: cosUrl 由 uploadToCOS 后填入
      source: 'upload'
    });
    this.errorMessage('');
    this.success(false);
    m.redraw();
  }

  /**
   * v0.4.7 #364 — 打开装备历史 modal
   * 选中装备复用 COS URL (不重传), 直接 push 到 equipmentPhotos
   * 标记 source='history' 让 startFusion 跳过 uploadToCOS
   */
  openEquipmentHistoryModal() {
    app.modal.show(EquipmentHistoryModal, {
      onSelect: data => {
        this.handleHistoryEquipmentSelect(data);
      }
    });
  }

  /**
   * 处理装备历史选择 — push 到 equipmentPhotos (source='history', 不重传)
   * 查重 — 同一 equipment_url 不能重复 push
   */
  handleHistoryEquipmentSelect(data) {
    if (!data || !data.equipment_url || !Array.isArray(data.types) || data.types.length === 0) {
      return;
    }

    // 查重 — 同一 equipment_url 已在列表则跳过
    const dup = this.equipmentPhotos.find(p => p.cosUrl === data.equipment_url);
    if (dup) {
      debugHelper.warn('[Seedream] Equipment already in list:', data.equipment_url);
      return;
    }
    const photoId = 'history_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    this.equipmentPhotos.push({
      id: photoId,
      file: null,
      // null = history source, 无 file
      dataUrl: data.thumbnail_url || data.equipment_url,
      // 缩略图优先, 兜底用原图
      types: [...data.types],
      cosUrl: data.equipment_url,
      // 复用 COS URL, 不重传
      source: 'history'
    });
    this.errorMessage('');
    this.success(false);
    m.redraw();
  }

  /**
   * v0.4.7 #364 — 记录装备使用到历史 (供下次复用)
   * fire-and-forget — 不阻塞 UI
   * 历史来源 (source='history') 的装备本来就在历史里, 跳过
   * 新上传的装备记录 use_count++ + last_used_at (后端按 user_id+equipment_url 查重)
   */
  async recordEquipmentHistory(uploadedUrls) {
    if (!Array.isArray(uploadedUrls)) return;
    for (let i = 0; i < uploadedUrls.length; i++) {
      const photo = this.equipmentPhotos[i];
      if (!photo) continue;
      // 历史来源跳过 (已经在历史里, 后端 use_count++ 不会自己触发, 跳过避免无意义请求)
      if (photo.source === 'history') continue;
      if (!photo.types || photo.types.length === 0) continue;
      try {
        await app.request({
          method: 'POST',
          url: '/api/ziven/seedream/equipment-history',
          body: {
            equipment_url: uploadedUrls[i],
            thumbnail_url: uploadedUrls[i],
            // 复用同一 URL (没有专门 thumbnail)
            types: photo.types
          }
        });
      } catch (err) {
        // 静默失败 — 记录历史是 best-effort, 不影响主流程
        debugHelper.warn('[Seedream] Equipment history record failed:', err);
      }
    }
  }

  /**
   * 上传图片到COS
   */
  async uploadToCOS(file) {
    try {
      // 获取COS签名配置
      const configResponse = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/cos/signature'
      });
      if (!configResponse.success || !configResponse.data) {
        console.error('[COS] Failed to get signature');
        return null;
      }
      const config = configResponse.data;

      // 生成唯一文件名
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substr(2, 9);
      const fileExt = file.name && file.name.split('.').pop() || 'jpg';
      const key = "seedream/".concat(timestamp, "_").concat(randomStr, ".").concat(fileExt);

      // 使用XMLHttpRequest进行表单上传
      //重要：腾讯云COS要求表单字段顺序必须与Policy中的顺序一致
      const formData = new FormData();
      const keyTime = config.q_key_time || config.start_time + ';' + config.expire_time;

      // 字段顺序必须与Policy conditions中的键名字典顺序一致
      // 注意：字典序中 'key' (k=107) < 'q' (q=113)，所以key在最前面
      // 顺序: key → q-ak → q-key-time → q-sign-algorithm → q-sign-time → policy → q-signature → file
      const signatureValue = config.signature;
      formData.append('key', key);
      formData.append('q-ak', config.q_ak || config.secret_id);
      formData.append('q-key-time', keyTime);
      formData.append('q-sign-algorithm', 'sha1');
      formData.append('q-sign-time', keyTime);
      formData.append('policy', config.policy);
      formData.append('q-signature', signatureValue);
      formData.append('file', file);
      debugHelper.log('[COS] Debug upload config:', {
        key: key,
        policy: config.policy ? config.policy.substring(0, 100) + '...' : 'missing',
        q_sign_algorithm: 'sha1',
        q_key_time: keyTime ? keyTime.substring(0, 20) + '...' : 'missing',
        q_ak: config.q_ak || config.secret_id,
        q_signature: signatureValue ? signatureValue.substring(0, 20) + '...' : 'missing',
        q_signature_source: 'signature'
      });

      // 构建COS上传地址（注意：腾讯云COS需要bucket-appid格式）
      let uploadUrl;
      if (config.app_id) {
        uploadUrl = "https://".concat(config.bucket, "-").concat(config.app_id, ".cos.").concat(config.region, ".myqcloud.com");
      } else {
        // 兼容没有app_id的情况
        uploadUrl = "https://".concat(config.bucket, ".cos.").concat(config.region, ".myqcloud.com");
      }
      debugHelper.log('[COS] Upload URL:', uploadUrl);
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', uploadUrl, true);
        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 204) {
            // 上传成功，构建CDN URL
            let url;
            if (config.cdn_domain) {
              url = rtrim(config.cdn_domain, '/') + '/' + key;
            } else if (config.app_id) {
              // 使用带app_id的COS默认域名
              url = "https://".concat(config.bucket, "-").concat(config.app_id, ".cos.").concat(config.region, ".myqcloud.com/").concat(key);
            } else {
              url = uploadUrl + '/' + key;
            }
            debugHelper.log('[COS] Upload success:', url);
            resolve(url);
          } else {
            console.error('[COS] Upload failed:', xhr.status, xhr.responseText);
            reject(new Error('COS upload failed'));
          }
        };
        xhr.onerror = () => {
          console.error('[COS] Upload network error');
          reject(new Error('COS upload network error'));
        };
        xhr.send(formData);
      });
    } catch (error) {
      console.error('[COS] Upload error:', error);
      return null;
    }
  }

  /**
   * 重选装备（通过装备选择Modal）
   * @param {number} index - 装备照片数组中的索引
   */
  reselectEquipment(index) {
    debugHelper.log('[Seedream] reselectEquipment called, index:', index);
    const photo = this.equipmentPhotos[index];
    if (!photo) return;

    // 打开装备选择Modal，进入重选模式
    app.modal.show(EquipmentSelectModal, {
      selectedEquipments: [],
      // 不排除任何类型（重选模式下会显示所有）
      reselectMode: true,
      // 重选模式
      reselectIndex: index,
      // 当前索引
      reselectData: {
        // 预填充的数据
        file: photo.file,
        dataUrl: photo.dataUrl,
        types: photo.types
      },
      onSubmit: (file, dataUrl, types, idx) => {
        if (idx >= 0 && idx < this.equipmentPhotos.length) {
          this.equipmentPhotos[idx] = {
            id: this.equipmentPhotos[idx].id,
            file: file,
            dataUrl: dataUrl,
            types: types
          };
          debugHelper.log('[Seedream] Equipment reselected:', idx, types);
          m.redraw();
        }
      }
    });
  }

  /**
   * 处理重选文件
   */
  handleReselectFile(e) {
    const file = e.target.files[0];
    if (!file) return;

    // 从input的id属性获取索引（避免this.reselectingIndex可能丢失的问题）
    const inputIndex = parseInt(e.target.dataset.reselectIndex, 10);
    if (isNaN(inputIndex)) return;
    const index = inputIndex;

    // 验证文件类型
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.errorMessage(t('ziven-dress-up.forum.common.invalid_type'));
      e.target.value = '';
      return;
    }

    // 验证文件大小
    if (file.size > this.maxFileSize * 1024 * 1024) {
      this.errorMessage(tRich('ziven-dress-up.forum.common.file_too_large', {
        size: this.maxFileSize
      }));
      e.target.value = '';
      return;
    }
    const self = this;
    const reader = new FileReader();
    reader.onload = function (event) {
      const dataUrl = event.target.result;
      if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
        self.errorMessage(t('ziven-dress-up.forum.common.read_error'));
        e.target.value = '';
        return;
      }

      // 验证 magic bytes
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data) {
        self.errorMessage(t('ziven-dress-up.forum.common.invalid_format'));
        e.target.value = '';
        return;
      }
      try {
        const binaryString = atob(base64Data.substring(0, 24));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const isValidImage = self.validateImageMagicBytes(bytes, file.type);
        if (!isValidImage) {
          self.errorMessage(t('ziven-dress-up.forum.common.invalid_content'));
          e.target.value = '';
          return;
        }
      } catch (err) {
        self.errorMessage(t('ziven-dress-up.forum.common.invalid_content_short'));
        e.target.value = '';
        return;
      }

      // 更新装备照片
      if (index >= 0 && index < self.equipmentPhotos.length) {
        self.equipmentPhotos[index].file = file;
        self.equipmentPhotos[index].dataUrl = dataUrl;
      }
      self.errorMessage('');
      self.success(false);
      m.redraw();
    };
    reader.onerror = function () {
      self.errorMessage(t('ziven-dress-up.forum.common.read_error'));
      e.target.value = '';
      m.redraw();
    };
    reader.readAsDataURL(file);
  }
}
flarum.reg.add('ziiven-dress-up', 'forum/components/SeedreamPage', SeedreamPage);